// ─────────────────────────────────────────────────────────────────────────────
// deviceService.js — Stable device ID for this browser/machine
//
// Generates a UUID v4 on first use and persists it in localStorage.
// Sent as X-Device-ID on every API request and in the connect/disconnect body.
//
// Lifecycle:
//   - Generated once per browser profile, survives logout and page refresh
//   - Only cleared if the user manually clears localStorage or calls clearDeviceId()
//   - NOT cleared on logout — the backend associates circuits with device_id,
//     so keeping the same ID across sessions avoids orphaned circuit records
// ─────────────────────────────────────────────────────────────────────────────

const STORAGE_KEY = "npq_device_id";

// ── UUID v4 generator (no dependency needed — crypto.randomUUID is baseline) ──
function generateUUID() {
  if (typeof crypto !== "undefined" && crypto.randomUUID) {
    return crypto.randomUUID();
  }
  // Fallback for older browsers
  return "xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx".replace(/[xy]/g, (c) => {
    const r = (Math.random() * 16) | 0;
    const v = c === "x" ? r : (r & 0x3) | 0x8;
    return v.toString(16);
  });
}

/**
 * Returns the stored device ID, creating and persisting one if it doesn't exist.
 * Safe to call on every render — reads from localStorage synchronously.
 */
export function getOrCreateDeviceId() {
  let id = localStorage.getItem(STORAGE_KEY);
  if (!id) {
    id = generateUUID();
    localStorage.setItem(STORAGE_KEY, id);
  }
  return id;
}

/**
 * Clears the stored device ID.
 * Next call to getOrCreateDeviceId() will generate a fresh UUID.
 * Only call this on explicit device de-registration — NOT on normal logout.
 */
export function clearDeviceId() {
  localStorage.removeItem(STORAGE_KEY);
}
